import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	storeSearchClientPortalApproval
} from 'store/reducers/mainSlice';
import PrivacyModeContent from 'ui-component/PrivacyModeContent';
import { FaSearch } from 'react-icons/fa';
import Filter from 'ui-component/filter-menu/Filter';
import moment from 'moment';
import { Skeleton } from '@mui/material';
import { CountriesUIFlag } from 'ui-component/CountriesUI';
import { CommonDateOnlyFormat } from 'helpers/common.helper';
import { useQueryClient } from 'react-query';

import {
	ChevronLeftIcon,
} from 'ui-component/custom-icons/icons';
import usePageTitle from 'hooks/usePageTitle';
import SortableHeader from 'ui-component/SortableHeader';

import NoRecordFoundIcon from 'assets/images/icons/no-records-found.svg';
import StatusBadge from 'ui-component/StatusBadge';

import DetailModal from './DetailModal';
import useIsMobile from 'utils/isMobileUtils';
import DetailModalMobile from './DetailModalMobile';
import { TbDatabaseExclamation } from 'react-icons/tb';

// ✅ Clear
const contactsApprove = () => {
	const [setPageTitle] = usePageTitle('Client Portal Request');
	const [activeComponent, setActiveComponent] = useState('All');
	const dispatch = useDispatch();

	const [searchField, setSearchField] = useState();
	const [searchValue, setSearchValue] = useState();
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [filters, setFilters] = useState([]);
	const [activeFilters, setActiveFilters] = useState({});
	const [sortByFilter, setSortByFilter] = useState();
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [selectedData, setSelectedData] = useState(null);



	const storedSearch = useSelector((state) => state.main.searchClientPortalApproval);
	const storedFilter = useSelector((state) => state.main.filterClientPortalApproval);

	useEffect(() => {

		const fetchData = async () => {
			if (storedSearch) {
				await handleSearch({ target: { value: storedSearch } });
			}
		};

		fetchData();
	}, [storedSearch, searchField, activeFilters, storedFilter]);

	useEffect(() => {
		initialRender()
		setPageTitle('Client Approval')
		setActiveComponent('Client Approval');

	}, []);

	useEffect(() => {
		return () => {
			setPageTitle(null);
		};
	}, []);

	const queryClient = useQueryClient();
	const [isLoading, setIsLoading] = useState(false);


	const initialRender = () => {
		getFiltersList();
	};

	const getFiltersList = () => {
		api.getContactClientFilter().then((res) => {
			setFilters(res.data);
		});
	};

	useEffect(() => {
		setActiveComponent('Client Approval');
	}, []);


	useEffect(() => {
		queryClient.invalidateQueries({ queryKey: ['contact-approval-list-counter'] });
	}, [activeComponent]);

	const handleClose = () => {
		setShowDetailModal(false)
		setSelectedData(null);
	}

	const handleSearch = (e) => {
		let text = e.target.value;
		
		setSearchValue(text);
		setIsLoading(true);	
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		// Only update searchField after user stops typing for 500ms
		let timeout = setTimeout(() => {
			dispatch(storeSearchClientPortalApproval(text));
			setSearchField(text)	
		}, 500);

		setSearchTimeout(timeout);

	};

	const isMobile = useIsMobile();


	const updateSortByStatus = (name) => {
		if (sortByFilter?.field == name) {
			setSortByFilter((e) => ({
				...e,
				direction: e?.direction == 'asc' ? 'desc' : 'asc'
			}));
		} else {
			setSortByFilter((e) => ({
				field: name,
				direction: 'asc'
			}));
		}
	};
	
	

	const [dataList, setDataList] = useState([]);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const tableRef = useRef();
	const [showPreloadingRows, setShowPreloadingRows] = useState(true);
	const [totalRecords, setTotalRecords] = useState(0);


	useEffect(() => {
		// Only run fetchData after user stops typing (debounced by searchTimeout)
		// setIsLoading(true);
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

				setShowPreloadingRows(true);
				setIsLoading(true);
		const timeout = setTimeout(() => {
			const fetchData = async () => {
				if (storedSearch) {
					await handleSearch({ target: { value: storedSearch } });
				}
				setDataList([]);
				initializeData();
				getFiltersList();
			};
			fetchData();
		}, 500); // 500ms debounce

		setSearchTimeout(timeout);

		// Cleanup on unmount or before next effect run
		return () => clearTimeout(timeout);
		// Only trigger when these change
	}, [storedSearch, searchField, storedFilter]);

	useEffect(() => {
		setIsLoading(true);
	}, [activeFilters])

	useEffect(() => {
		const handleScroll = () => {
			if (!tableRef.current || showPreloadingRows || !hasMore) return;
			const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
			// Trigger only when user reaches the bottom (with a small threshold)
			if (scrollTop + clientHeight >= scrollHeight - 10) {
				loadMoreData();
			}
		};
		const currentRef = tableRef.current;
		if (currentRef) {
			currentRef.addEventListener('scroll', handleScroll);
		}
		return () => {
			if (currentRef) {
				currentRef.removeEventListener('scroll', handleScroll);
			}
		};
	}, [showPreloadingRows, hasMore, dataList]);


    const loadMoreData = () => {
        if (showPreloadingRows || !hasMore) return;
        setShowPreloadingRows(true);
        let request = {};
        request.page = page + 1;
        request.search = searchField?.length > 2 ? searchField : null;

        
        api.getClientRequestListsV2(request, activeFilters, searchField, sortByFilter).then((res) => {
            // Use pagination meta from backend
            const nextPage = res?.data?.data?.meta?.current_page || request.page;
            const lastPage = res?.data?.data?.meta?.last_page || nextPage;
            if (nextPage > lastPage || res?.data?.data?.data?.length === 0) {
                setHasMore(false);
            } else {
                setDataList(prev => [...prev, ...res?.data?.data?.data || []]);
                setPage(nextPage);
            }
            setShowPreloadingRows(false);
        });
	};
	


	const initializeData = () => {
		// Initial load
		let request = {}
		request.page = 1;
		request.search = searchField?.length > 2 ? searchField : null;
		
		api.getClientRequestListsV2(request, activeFilters, searchField, sortByFilter).then((res) => {
			
			setDataList([]);
			setTimeout(() => {
				setDataList(res?.data?.data?.data || []);

				setHasMore(true);
				setPage(1);
				setIsLoading(false);
				setShowPreloadingRows(false);
				setTotalRecords(res?.data?.data?.total || 0);
			}, 300);
		});

	}
	

	return (
		<>
			<div className="row">
				<div className="col-xl-12 col-lg-12">
					<div className="show-on-mobile new-page-header" style={{ position: 'relative' }}>
						<div className="header-bubble" />
						<div className="d-flex flex-column gap-2 " style={{ position: 'relative', zIndex: 1, paddingTop: 10, width: '100%' }}>
							<h5 style={{ textAlign: 'center' }}>Client Portal</h5>
							<div style={{ paddingLeft: 20, paddingRight: 20, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, paddingBottom: 4 }}>
							</div>
						</div>
					</div>
					<div className="section-head-new pl-0 mar10201316 hide-on-mobile">
						<ul className="topListCom">
							<Link to={'/contacts'} className="btn btn-primary btn-outlined-main-primary btn-create-report">
								<ChevronLeftIcon height={'13px'} width={'13px'} />
								Contacts
							</Link>
						</ul>
						<div className="actions d-none d-md-flex">
						</div>
					</div>
					<div
						style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, paddingTop: 0, paddingBottom: 5, paddingLeft: 20, paddingRight: 20 }}
						className="show-on-mobile"
					>
						<form className="form-inline navbar-search" style={{ flex: 1 }}>
							<div style={{ position: 'relative', width: '100%' }}>
								<input
									type="text"
									className="form-control bg-white border"
									placeholder="Search"
									aria-label="Search"
									aria-describedby="basic-addon2"
									value={searchValue || ''}
									onChange={handleSearch}
									style={{ width: '100%', paddingRight: 36 }}
								/>
								<span
									className="searchIcon"
									style={{
										position: 'absolute',
										right: 10,
										top: '50%',
										transform: 'translateY(-50%)',
										color: '#888',
										pointerEvents: 'none'
									}}
								>
									<FaSearch />
								</span>
							</div>
						</form>
						<div style={{ flexShrink: 0 }}>
						</div>
					</div>
					<div className={`card shadow-sm  bg-white mb-4 table-card ${isMobile ? 'shorter-table' : ''}`}>
						<div className=" align-items-center justify-content-between TableSerachBar">
							<div>
								<Filter
									activeFilters={activeFilters}
									onSubmit={setActiveFilters}
									filters={filters}
									multi={undefined}
									source={'client_portal_approval'}
								/>
							</div>
							<form className="form-inline navbar-search" style={{ display: 'flex', gap: '5px' }} onSubmit={(e) => e.preventDefault()}>
								<div className="input-group">
									<input
										type="text"
										className="form-control bg-white border"
										placeholder="Search"
										aria-label="Search"
										aria-describedby="basic-addon2"
										value={searchValue || ''}
										onChange={handleSearch}
										style={{ width: '100%' }}
									/>
									<span className="searchIcon">
										<FaSearch />
									</span>
								</div>
							</form>
						</div>
						<div className="estimation-table-area">
							<div className="table-side">

								{
									!isMobile ? (
										<div
											className="table-responsive-xl tableData estimationList"
											ref={tableRef}
											style={{
												height: 'calc(100vh - 260px)',
												overflowY: 'auto',
												scrollbarWidth: 'thin',
												scrollbarColor: '#0966ff transparent'

											}}
										>
											<table className="table  min-width-unset tbl-space pm-table d-none d-md-table">
												<thead>
													<tr className="sticky-header">
														<th
															scope="col" 
															style={{ width: '50px' }}
														>
															Country
														</th>
														<th scope="col" width="">
															<SortableHeader
																name="company_name"
																currentSortBy={sortByFilter}
																onClick={() => {
																	updateSortByStatus('company_name');
																}}
															>
																Company
															</SortableHeader>
														</th>

														{/* <th scope="col" width="">
															<SortableHeader
																name="name"
																currentSortBy={sortByFilter}
																onClick={() => {
																	updateSortByStatus('name');
																}}
															>
																Name
															</SortableHeader>
														</th> */}
														<th scope="col" width="" className="text-center">
															<SortableHeader
																name="email"
																currentSortBy={sortByFilter}
															>
																Contact Details
															</SortableHeader>
														</th>
														<th scope="col">
															<div style={{ width: '90px' }}>

																Onboarding %
															</div>
														</th>
														<th scope="col" className="text-center">
															<div style={{ width: '80px' }}>

																<SortableHeader
																	name="status"
																	currentSortBy={sortByFilter}
																	onClick={() => {
																		updateSortByStatus('status');
																	}}
																	style={{ maxWidth: '80px' }}
																>
																	Status
																</SortableHeader>
															</div>
														</th>
														<th scope="col" style={{ width: '135px' }}>
															<SortableHeader
																name="created_at"
																currentSortBy={sortByFilter}
																onClick={() => {
																	updateSortByStatus('created_at');
																}}
															>
																Date Registered
															</SortableHeader>
															
														</th>

													</tr>
												</thead>
												<tbody>
													{isLoading ? (
														[...Array(50)].map((e, i) => (
															<tr style={{ height: '35px' }} key={i}>
																{[...Array(6)].map((e, i) => (
																	<td>
																		<Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }}></Skeleton>
																	</td>
																))}
															</tr>
														))
													) : (
														<>
															{dataList?.length > 0 && (
																dataList?.map((item, index) => {
																	return (
																		<tr key={index}>
																			<td
																				className='cursor-pointer align-middle font14'
																				onClick={() => {
																					setShowDetailModal(true)
																					setSelectedData(item);
																				}}
																			>
																									
																				{item?.country ? (
																					<div
																						style={{
																							fontSize: '20px',
																							background: '#f2f4f6',
																							borderRadius: '8px',
																							width: '37px'
																						}}
																						className="justify-content-center d-flex align-items-center"
																					>
																						<CountriesUIFlag countryName={`${item?.country}`} />
																					</div>
																				) : (
																					<div
																						style={{
																							fontSize: '20px',
																							background: '#f2f4f6',
																							borderRadius: '8px',
																							width: '37px',
																							height: '37px'
																						}}
																					></div>
																				)}
																			</td>
																			<td
																				className='cursor-pointer align-middle font14'
																				onClick={() => {
																					setShowDetailModal(true)
																					setSelectedData(item);
																				}}
																			>
																				<div>
																					<div className="fw-500">{item?.company_name}</div>
																					<div className="text-gray-700">{`${item?.first_name} ${item?.last_name}`}</div>
																				</div>
																				
																			</td>
																			<td
																				className='cursor-pointer align-middle font14'
																				onClick={() => {
																					setShowDetailModal(true)
																					setSelectedData(item);
																				}}
																			>
																				<div>
																					<div>{item?.email}</div>
																					<div className="text-gray-700">{item?.number}</div>
																				</div>
																				
																			</td>
																			<td
																				style={{ width: '90px' }}
																				className="cursor-pointer align-middle font14 text-center"
																				onClick={() => {
																					setShowDetailModal(true)
																					setSelectedData(item);
																				}}
																			>
																				{`${item?.onboarding_progress}%`}
																			</td>
																			<td
																				style={{ width: '110px' }}
																				className="cursor-pointer align-middle font14"
																				onClick={() => {
																					setShowDetailModal(true)
																					setSelectedData(item);
																				}}
																			>
																				<div style={{ width: '80px' }}>
																					<StatusBadge itemStatus={item?.status} />
																				</div>
																			</td>
																			<td
																				className="cursor-pointer align-middle font14"
																				onClick={() => {
																					setShowDetailModal(true)
																					setSelectedData(item);
																				}}
																			>
																				{moment(item?.created_at).format(CommonDateOnlyFormat)}
																			</td>
																		</tr>
																	);
																})
															)}
															{showPreloadingRows && (
																[...Array(100)].map((e, i) => (
																	<tr style={{ height: '35px' }} key={i}>
																		{[...Array(6)].map((e, i) => (
																			<td>
																				<Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
																			</td>
																		))}
																	</tr>
																))
															)}
															{(dataList?.length < 1 && !isLoading) && (
																<tr className="no-hover">
																	<td colSpan={6} className="whitebg">
																		<div className="not-found-container">
																				<div className="not-found-icon">
																				<img src={NoRecordFoundIcon} style={{ height: '200px', width: '200px' }} />
																				
																			</div>
																			<h6 className="not-found-text">No Data Available</h6>
																		</div>
																	</td>
																</tr>
															)}
														</>
													)}
												</tbody>
											</table>
										</div>
									) : (
										<div
											className="picklists-mobile-list"
											ref={tableRef}
											style={{
												height: `calc(100dvh - ${Object.keys(activeFilters).length === 0 ? '275px' : '332px' })`,
												overflowY: 'auto',
												display: 'flex',
												flexDirection: 'column'
											}}
										>
											{isLoading ? (
												[...Array(7)].map((_, i) => (
													<div className="inv-cards-list d-md-none" style={{ padding: '12px 0' }}>
														{[...Array(15)].map((_, idx) => (
															<div
																key={idx}
																className="order-list-card"
																style={{
																	background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
																	marginBottom: 10,
																	borderRadius: 12,
																	padding: 16,
																	minHeight: 90,
																	display: 'flex',
																	flexDirection: 'column',
																	gap: 10
																}}
															>
																{[...Array(3)].map((__, i) => (
																	<div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
																		<Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
																	</div>
																))}
															</div>
														))}
													</div>
												))
											) : (
												<>
													{dataList.map((row, index) => (
														<div
															style={{
																display: 'flex',
																padding: '5px 20px',
																flexDirection: 'row',
																background: index % 2 === 0 ? '#f9f9f9' : '#fff',
																borderBottom: '1px solid #eee',
																justifyContent: 'space-between'
															}}
															key={row.id || index}
															onClick={() => {
																setShowDetailModal(true)
																setSelectedData(row);
															}}
														>
															<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 10 }}>
																<div style={{ display: 'flex', flexDirection: 'column', gap: 5, width: '100%' }}>
																	<div style={{ display: 'flex', gap: 5, fontWeight: 500 }}>
																		<div style={{ color: '#316aff' }}>
																			<PrivacyModeContent>{row.company_name}</PrivacyModeContent>
																		</div>
																	</div>
																	<div className="picklist-mobile-row">
																		<div><PrivacyModeContent>{row.first_name} {row.last_name}</PrivacyModeContent></div>
																	</div>
																	<div className="picklist-mobile-row">
																		<div>{row.email}</div>
																	</div>
																	<div
																		className="picklist-mobile-row"
																		style={{
																			marginTop: `${row?.country ? '-5px' : '0'}` ,
																			marginBottom: `${row?.country ? '-5px' : '0'}`
																		}}
																	>
																		<div className="">
																		
																			{row?.country ? (
																				<div
																					style={{
																						fontSize: '18px',
																					}}
																					className="justify-content-center d-flex align-items-center"
																				>
																					<CountriesUIFlag countryName={`${row?.country}`} />
																				</div>
																			) : (
																				<div
																					style={{
																						fontSize: '20px',
																						background: '#f2f4f6',
																						borderRadius: '4px',
																						width: '18px',
																						height: '18px'
																					}}
																				></div>
																			)}
																		</div>
																		<div>{row.number}</div>
																	</div>
																</div>
																<div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
																	<div style={{ width: '90px' }}>
																		<div><StatusBadge itemStatus={row?.status} /></div>
																		<div
																			className="text-gray-700 text-right"
																			style={{
																				fontSize: 11,
																				marginTop: 3,
																			}}
																		>
																			{moment(row?.created_at).format(CommonDateOnlyFormat)}
																		</div>
																		
																		
																	</div>
																	<div className="picklist-mobile-actions">
																		
																	</div>
																</div>
															</div>
														</div>
													))}
													
													{showPreloadingRows && (
														[...Array(100)].map((_, idx) => (
															<div
																key={idx}
																className="order-list-card"
																style={{
																	background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
																	marginBottom: 10,
																	borderRadius: 12,
																	padding: 16,
																	minHeight: 90,
																	display: 'flex',
																	flexDirection: 'column',
																	gap: 10
																}}
															>
																{[...Array(3)].map((__, i) => (
																	<div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
																		<Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
																	</div>
																))}
															</div>
														))
													)}
													{(dataList?.length < 1 && !isLoading) && (
														<div className="w-100">
															<div className="not-found-container">
																<div className="not-found-icon">
																	<TbDatabaseExclamation />
																</div>
																<h6 className="not-found-text">No Data Available</h6>
															</div>
														</div>
													)}
												</>
											)}
										</div>
									)
								}

								<div
									className="hide-on-mobile"
									style={{
										padding: '10px 0px 10px 20px',
										borderTop: '1px solid #e0e0e0e0',
										display: 'flex',
										gap: '5px',
										justifyContent: 'space-between',
										height: '45px',
									}}
								>

									<div className="flex align-items-center" style={{ gap: '10px' }}>
									</div>
									<div className="flex align-items-center ml-auto" style={{ gap: '5px', paddingRight: '25px' }}>
										{isLoading ? (
											<div style={{ width: '100px' }}>
												<Skeleton variant="rounded" width={'100%'} height={20}></Skeleton>
											</div>
										) : (
											<>
												<div className="text-gray-800">Total Record:</div>
												<div className="fw-500">
														{totalRecords.toLocaleString('en-US')}
												</div>
											</>
										)}
									</div>
								</div>
								{/* {isLoading ? (
									<div
										className="d-flex align-items-center justify-content-center paginationcon"
										style={{
											padding: '5px 0',
											marginBottom: '-8px'
										}}
									>
										<div className="col-sm-12 col-md-6"></div>
										<div className="col-sm-12 col-md-6">
											<div style={{ display: 'flex', justifyContent: 'right', alignItems: 'center' }}>
												<div style={{ marginRight: '5px' }}>
													<div style={{ width: '150px' }}>
														<Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '12px' }}></Skeleton>
													</div>
												</div>
												<div>
													<div style={{ width: '70px' }}>
														<Skeleton variant="rounded" width={'100%'} height={35} style={{ borderRadius: '12px' }}></Skeleton>
													</div>
												</div>
											</div>
										</div>
									</div>
								) : (
									<Pagination pagination={paginationData} data={handleData} onLoading={(e) => {}} reactQuery />
								)} */}
							</div>
						</div>
					</div>
				</div>
			</div>

            {showDetailModal && !isMobile && (
				<DetailModal
					isOpen={showDetailModal}
					onClose={handleClose}
					selectedId={selectedData?.id}
					isApproved={selectedData?.is_approved}
					onRemove={(clientId) => {
						setDataList((prevList) =>
							prevList.filter((row) => row.id !== clientId)
						);
					}}
					onApprove={(clientId) => {
						setDataList((prevList) =>
							prevList.map((row) =>
								row.id === clientId 
									? { ...row, status: 'Approved' }
									: row
							)
						);
					}}
				/>
            )}
            {showDetailModal && isMobile && (
				<DetailModalMobile
					isOpen={showDetailModal}
					onClose={handleClose}
					selectedId={selectedData?.id}
					isApproved={selectedData?.is_approved}
					onRemove={(clientId) => {
						setDataList((prevList) =>
							prevList.filter((row) => row.id !== clientId)
						);
					}}
					onApprove={(clientId) => {
						setDataList((prevList) =>
							prevList.map((row) =>
								row.id === clientId 
									? { ...row, status: 'Approved' }
									: row
							)
						);
					}}
				/>
            )}
		</>
	);
};

export default contactsApprove;
